import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { toDate } from 'date-fns-tz';
import { OnboardingUser } from '../services/HxcStagingService';
import { logger } from 'utils/logger';

const log = logger.getLogger('EditOnboardingDateDialog');

interface EditOnboardingDateDialogProps {
  open: boolean;
  onClose: () => void;
  user: OnboardingUser | null;
  onSave: (userId: number, onboardDate: string) => Promise<void>;
}

type DialogState = 'editing' | 'saving' | 'success' | 'error';

const EditOnboardingDateDialog: React.FC<EditOnboardingDateDialogProps> = ({
  open,
  onClose,
  user,
  onSave
}) => {
  const [dialogState, setDialogState] = useState<DialogState>('editing');
  const [onboardDate, setOnboardDate] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [autoCloseTimer, setAutoCloseTimer] = useState<number | null>(null);

  // Reset state when dialog opens or user changes
  useEffect(() => {
    if (open && user) {
      setDialogState('editing');
      setOnboardDate(user.onboardDate ? new Date(user.onboardDate) : null);
      setError(null);
      setAutoCloseTimer(null);
    }
  }, [open, user]);

  const handleClose = useCallback(() => {
    if (dialogState !== 'saving') {
      onClose();
    }
  }, [dialogState, onClose]);

  // Handle auto-close after success
  useEffect(() => {
    if (dialogState === 'success') {
      const timer = setTimeout(() => {
        handleClose();
      }, 2000); // 2 seconds
      setAutoCloseTimer(2);
      
      const countdown = setInterval(() => {
        setAutoCloseTimer(prev => {
          if (prev === null || prev <= 1) {
            clearInterval(countdown);
            return null;
          }
          return prev - 1;
        });
      }, 1000);

      return () => {
        clearTimeout(timer);
        clearInterval(countdown);
      };
    }
  }, [dialogState, handleClose]);

  const handleSave = async () => {
    if (!user || !onboardDate) {
      setError('Please select a date');
      return;
    }

    try {
      setDialogState('saving');
      setError(null);
      
      // Build date string for Hawaii midnight
      const year = onboardDate.getFullYear();
      const month = String(onboardDate.getMonth() + 1).padStart(2, '0');
      const day = String(onboardDate.getDate()).padStart(2, '0');
      const hawaiiDateString = `${year}-${month}-${day} 00:00:00`;
      
      // Interpret AS Hawaii time and get Date object
      const date = toDate(hawaiiDateString, { timeZone: 'Pacific/Honolulu' });
      
      // Convert to UTC ISO string without milliseconds
      const formattedDate = date.toISOString().slice(0, 19) + 'Z';
      
      log.log(`Updating onboard date for user ${user.id} to:`, formattedDate);
      
      await onSave(user.id, formattedDate);
      
      setDialogState('success');
      log.log('Successfully updated onboard date');
    } catch (err: any) {
      log.error('Error updating onboard date:', err);
      setError(err.message || 'Failed to update onboard date. Please try again.');
      setDialogState('error');
    }
  };

  if (!user) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      {dialogState === 'editing' && (
        <>
          <DialogTitle>Edit Onboard Date</DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                User: <strong>{user.fullname}</strong> ({user.username})
              </Typography>
              
              <Box sx={{ mt: 3 }}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="Onboarding Date"
                    value={onboardDate}
                    onChange={(newValue) => setOnboardDate(newValue)}
                    minDate={new Date()}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true
                      }
                    }}
                  />
                </LocalizationProvider>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={handleClose}>Cancel</Button>
            <Button 
              onClick={handleSave} 
              variant="contained" 
              color="primary"
              disabled={!onboardDate}
            >
              Save
            </Button>
          </DialogActions>
        </>
      )}

      {dialogState === 'saving' && (
        <>
          <DialogTitle>Saving...</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
              <CircularProgress size={60} />
              <Typography variant="body1" sx={{ mt: 3 }}>
                Updating onboard date...
              </Typography>
            </Box>
          </DialogContent>
        </>
      )}

      {dialogState === 'success' && (
        <>
          <DialogTitle>Date Updated</DialogTitle>
          <DialogContent>
            <Box sx={{ py: 2 }}>
              <Typography variant="body1" color="success.main" gutterBottom>
                ✓ Successfully updated onboard date!
              </Typography>
              {autoCloseTimer !== null && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  Closing in {autoCloseTimer} second{autoCloseTimer !== 1 ? 's' : ''}...
                </Typography>
              )}
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={handleClose} variant="contained">
              Close
            </Button>
          </DialogActions>
        </>
      )}

      {dialogState === 'error' && (
        <>
          <DialogTitle>Error Updating Date</DialogTitle>
          <DialogContent>
            <Box sx={{ py: 2 }}>
              <Typography variant="body1" color="error.main" gutterBottom>
                ❌ Failed to update onboard date
              </Typography>
              {error && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  {error}
                </Typography>
              )}
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={handleClose} variant="contained">
              Close
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export default EditOnboardingDateDialog;
